import { TFunction } from 'i18next';
import { CreateMeProductRequest, MeProductApiModel } from '~/lib/api/me/types';
import { getLocalStorageValue, setLocalStorageValue } from '~/hooks/use-local-storage';
import { LocalStorageKeys } from '~/apps/smartsend/lib/consts/LocalStorageKeys';
import { ProductFormState } from './types';

export const emptyForm: ProductFormState = {
  description: '',
  sku: '',
  weight: '',
  height: '',
  width: '',
  length: '',
  value: '',
  countryOfManufacture: '',
  tariffCode: '',
  shipsInternationally: false
};

// defaultCountryOfManufacture fills in for products that don't have one set yet (new products,
// or existing ones saved before this field existed) - it's the tenant's configured origin country.
export const productToForm = (product: MeProductApiModel, defaultCountryOfManufacture: string): ProductFormState => ({
  description: product.contents ?? '',
  sku: product.sku ?? '',
  weight: product.weight?.toString() ?? '',
  height: product.height?.toString() ?? '',
  width: product.width?.toString() ?? '',
  length: product.length?.toString() ?? '',
  value: product.estimatedValue?.toString() ?? '',
  countryOfManufacture: product.countryOfManufacture || defaultCountryOfManufacture,
  tariffCode: product.tariffCode ?? '',
  shipsInternationally: !!(product.countryOfManufacture || product.tariffCode)
});

// The backend doesn't reliably persist a "default" flag on the product itself, so the default
// product is tracked client-side instead. This is the single source of truth for "is this the default".
export const isDefault = (id: string | number | null | undefined) =>
  id != null && getLocalStorageValue<string>(LocalStorageKeys.MyProductsDefaultId) === String(id);

export const setDefaultProductId = (id: number | null) =>
  setLocalStorageValue<string | null>(LocalStorageKeys.MyProductsDefaultId, id != null ? String(id) : null);

// The backend requires every field on every save, or it treats an omitted field as cleared.
// countryOfManufacture/tariffCode are user-editable here; description/manufacturerProductId/
// standardisedProductId have no UI yet, so they're round-tripped from the product being edited
// (undefined/null for a new or duplicated product) so a save never silently wipes them.
export const buildProductPayload = (
  form: ProductFormState,
  existing?: MeProductApiModel | null
): CreateMeProductRequest => ({
  contents: form.description,
  sku: form.sku || null,
  weight: form.weight ? parseFloat(form.weight) : null,
  height: form.height ? parseFloat(form.height) : null,
  width: form.width ? parseFloat(form.width) : null,
  length: form.length ? parseFloat(form.length) : null,
  estimatedValue: form.value ? parseFloat(form.value) : null,
  countryOfManufacture: form.shipsInternationally ? form.countryOfManufacture || null : null,
  tariffCode: form.shipsInternationally ? form.tariffCode || null : null,
  description: existing?.description ?? null,
  manufacturerProductId: existing?.manufacturerProductId ?? null,
  standardisedProductId: existing?.standardisedProductId ?? null
});

export const productActionError = (
  t: TFunction<string, string>,
  result: { error?: { message: string } | null },
  actionKey: string,
  actionDefault: string
) =>
  result.error?.message ||
  t('PRODUCT_ACTION_ERROR', {
    defaultValue: 'Something went wrong {{action}}. Please try again.',
    action: t(actionKey, { defaultValue: actionDefault })
  });
